import React, { useState, useMemo, useEffect } from 'react';
import { Grid, Paper, Typography, TextField, Box, Button, CircularProgress, useTheme, Tooltip } from '@mui/material';
import { useGetAtgQuery, useGetATGPosSalesByDayQuery, useGetATGPosSalesByMonthQuery } from '../../../../../features/atgs/atgsApiSlice';
import { useGetAtgMeterReadingsByAtgNameQuery, useUpdateAtgMeterReadingsMutation } from '../../../../../features/organizations/organizationsApiSlice';
import generateSIRPDF from '../components/SIRPDFReport';
import generateLeakTestPDF from '../components/LeakTestPDFReport';
import usfuelblack from '../../../../../assets/usfuelblack.png';
import LeakTestReports from '../components/LeakTestReports';
import { tokens } from '../../../../../theme';

const DailySummary = ({ isReportsLoading, data, isErrorReports, deliveries, productLabels, selectedDate, atg_name, shiftStartTime, tankConfig }) => {
    const [meterReadings, setMeterReadings] = useState({});
    const [showManualOverride, setShowManualOverride] = useState(false);
    const [updateAtgMeterReadings] = useUpdateAtgMeterReadingsMutation();
    const { data: fetchedMeterReadings, isLoading, isError } = useGetAtgMeterReadingsByAtgNameQuery({ atg_name });
    const [logoImg, setLogoImg] = useState(null);

    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const selectedDateString = selectedDate.toISOString().split('T')[0];

    // POS sales snapshot (device shadow) for the selected day
    const { data: posSalesForDay, isFetching: isFetchingPosSales } = useGetATGPosSalesByDayQuery({
        atg_name,
        date: selectedDateString,
        shiftStartHour: shiftStartTime ?? 0,
    });

    const { data: posSalesForMonth, isFetching: isFetchingPosSalesMonth } = useGetATGPosSalesByMonthQuery({
        atg_name,
        year: selectedDate.getFullYear(),
        month: selectedDate.getMonth() + 1, // 1-based
        shiftStartHour: shiftStartTime ?? 0,
    });
    const sirDisabled = isReportsLoading || isFetchingPosSalesMonth;
    console.log('Monthly POS sales data:', posSalesForMonth);


    // Load historical manual meter readings (fallback / for override form)
    useEffect(() => {
        if (!isLoading && fetchedMeterReadings) {
            const sel = selectedDate.toISOString().split('T')[0];
            const readingsForSelectedDate = fetchedMeterReadings.find(
                (reading) => new Date(reading.metric_date).toISOString().split('T')[0] === sel
            );
            if (readingsForSelectedDate) {
                const newMeterReadings = (readingsForSelectedDate.fuel_readings || []).reduce((acc, curr) => {
                    acc[curr.fuel_type] = Number(curr.amount_dispensed || 0);
                    return acc;
                }, {});
                setMeterReadings(newMeterReadings);
            } else {
                setMeterReadings({});
            }
        }
    }, [selectedDate, fetchedMeterReadings, isLoading]);

    // Fetch the logo PNG
    useEffect(() => {
        fetch(usfuelblack)
            .then((res) => res.blob())
            .then((blob) => {
                const reader = new FileReader();
                reader.readAsDataURL(blob);
                reader.onloadend = function () {
                    const base64data = reader.result;
                    setLogoImg(base64data);
                };
            });
    }, []);

    const { data: atgReturn } = useGetAtgQuery(atg_name, { pollingInterval: 30000 });
    const atgAttributes = atgReturn?.attributes;
    const organization_id = atgAttributes?.organization_id;

    // ---------- Helpers: grade mapping & midgrade handling ----------
    const normalize = (s = '') => (s || '').toString().toLowerCase().replace(/[^a-z0-9]+/g, '');
    const isDiesel = (label) => /diesel|diese?l1|d1/.test(normalize(label));
    const isRegular = (label) => /regular|unleaded|ul|87/.test(normalize(label));
    const isPremium = (label) => /(supreme|\bsuper\b|premium|93|91|\bsp\b)/.test(normalize(label));
    const isMidgrade = (label) => /(plus|extra|mid|midgrade|silver|special|unlplus|unleadedplus|89)/.test(normalize(label));

    const tankRoles = useMemo(() => {
        const roles = { regular: null, premium: null, diesel: null };
        Object.entries(productLabels || {}).forEach(([tankKey, label]) => {
            const L = label || tankKey;
            if (!roles.diesel && isDiesel(L)) roles.diesel = tankKey;
            else if (!roles.regular && isRegular(L)) roles.regular = tankKey;
            else if (!roles.premium && isPremium(L)) roles.premium = tankKey;
        });
        return roles;
    }, [productLabels]);


    const allocateGradesToTanks = (grades) => {
        if (!grades || grades.length === 0) return null;

        const MIDGRADE_SPLIT = 0.5;
        const perTank = {};
        Object.keys(productLabels || {}).forEach((tk) => (perTank[tk] = 0));

        const addToRole = (role, gallons) => {
            if (!gallons) return;
            const tk = tankRoles[role];
            if (tk) perTank[tk] = (perTank[tk] || 0) + gallons;
        };

        grades.forEach((g) => {
            const gallons = Number(g?.vol || 0);
            if (gallons <= 0) return;
            const label = g?.grade || g?.label || '';

            if (isDiesel(label)) addToRole('diesel', gallons);
            else if (isMidgrade(label)) {
                addToRole('regular', gallons * MIDGRADE_SPLIT);
                addToRole('premium', gallons * (1 - MIDGRADE_SPLIT));
            } else if (isPremium(label)) addToRole('premium', gallons);
            else if (isRegular(label)) addToRole('regular', gallons);
            else addToRole('regular', gallons); // unknown → regular as safe default
        });

        return perTank;
    };

    // Raw POS grades list
    const posGrades = useMemo(() => {
        const payload = posSalesForDay?.payload;
        const grades = payload?.state?.reported?.fuel_sales_daily?.grades || [];
        return grades.map((g) => ({
            label: g.grade,
            vol: Number(g.vol || 0),
            amt: Number(g.amt || 0),
            unit: Number(g.unit || 0),
        }));
    }, [posSalesForDay]);

    // Allocate midgrade to reg+prem, sum per tank
    const salesDerivedReadings = useMemo(() => {
        if (!posGrades || posGrades.length === 0) return null;

        const MIDGRADE_SPLIT = 0.5;
        const perTank = {};
        Object.keys(productLabels || {}).forEach((tk) => (perTank[tk] = 0));

        const addToRole = (role, gallons) => {
            if (!gallons) return;
            const tk = tankRoles[role];
            if (tk) perTank[tk] = (perTank[tk] || 0) + gallons;
        };

        posGrades.forEach((g) => {
            const gallons = Number(g.vol || 0);
            if (gallons <= 0) return;
            const label = g.label || '';

            if (isDiesel(label)) addToRole('diesel', gallons);
            else if (isMidgrade(label)) {
                addToRole('regular', gallons * MIDGRADE_SPLIT);
                addToRole('premium', gallons * (1 - MIDGRADE_SPLIT));
            } else if (isPremium(label)) addToRole('premium', gallons);
            else if (isRegular(label)) addToRole('regular', gallons);
            else addToRole('regular', gallons); // unknown gasoline → treat as midgrade-like to regular
        });

        return perTank;
    }, [posGrades, productLabels, tankRoles]);

    // Turn the monthly POS response into the exact array shape SIR expects:
    // [{ metric_date: 'YYYY-MM-DDT00:00:00.000Z', fuel_readings: [{fuel_type, amount_dispensed}, ...] }, ...]
    const monthlySalesReadingsArray = useMemo(() => {
        if (!posSalesForMonth) return null;

        // Tolerate a couple of server response shapes:
        // 1) Array of objects each containing `payload`
        // 2) Object { days: [...] } where each item contains `payload`
        const dayItems = Array.isArray(posSalesForMonth)
            ? posSalesForMonth
            : (posSalesForMonth?.days || []);

        const out = [];

        dayItems.forEach((item) => {
            const payload = item?.payload || item; // sometimes the item itself is the payload
            const fsd = payload?.state?.reported?.fuel_sales_daily;
            const dateStr = fsd?.date; // "YYYY-MM-DD"
            const grades = fsd?.grades || [];

            if (!dateStr) return; // skip unexpected records

            const perTank = allocateGradesToTanks(grades);
            if (!perTank) return;

            const fuel_readings = Object.entries(perTank).map(([tankKey, gallons]) => ({
                fuel_type: tankKey,
                amount_dispensed: Number(gallons || 0),
            }));

            // Keep metric_date as ISO (SIR code splits on 'T' safely)
            out.push({
                metric_date: new Date(dateStr).toISOString(),
                fuel_readings,
            });
        });

        // Sort by metric_date ascending (useful for table order)
        out.sort((a, b) => new Date(a.metric_date) - new Date(b.metric_date));
        return out;
    }, [posSalesForMonth, allocateGradesToTanks]);

    const overrideSelectedDayInMonthlyArray = (arr) => {
        if (!arr) return null;
        // Build a manual entry for the selected day
        const manualFuelReadings = Object.entries(meterReadings || {}).map(([tank, reading]) => ({
            fuel_type: tank,
            amount_dispensed: Number(reading || 0),
        }));
        const selDay = selectedDate.toISOString().split('T')[0]; // "YYYY-MM-DD"

        // Find the day's index in the monthly array and replace fuel_readings for that date
        const idx = arr.findIndex(d => (d.metric_date || '').startsWith(selDay));
        if (idx !== -1) {
            arr[idx] = {
                ...arr[idx],
                fuel_readings: manualFuelReadings,
            };
        } else {
            // If the selected day isn't present (edge case), append it
            arr.push({
                metric_date: new Date(selDay).toISOString(),
                fuel_readings: manualFuelReadings,
            });
            arr.sort((a, b) => new Date(a.metric_date) - new Date(b.metric_date));
        }
        return arr;
    };


    // Build effective “meter” readings (sales preferred, falls back to manual saved)
    const effectiveMeterReadings = useMemo(() => {
        const out = {};
        const tankKeys = Object.keys(
            (data || []).reduce((acc, report) => {
                const tks = Object.keys(report?.state?.reported?.tanks || {});
                tks.forEach((tk) => (acc[tk] = true));
                return acc;
            }, {})
        );
        tankKeys.forEach((tk) => {
            const salesVal = salesDerivedReadings?.[tk];
            out[tk] = (salesVal != null && !Number.isNaN(salesVal))
                ? Number(salesVal.toFixed(3))
                : (meterReadings[tk] || 0);
        });
        return out;
    }, [data, salesDerivedReadings, meterReadings]);
    const atgReportsOnly = useMemo(() => {
        return (data || []).filter(r => {
            const t = r?.state?.reported?.tanks;
            return t && typeof t === 'object' && Object.keys(t).length > 0;
        });
    }, [data]);

    // ---------- Tank/Delivery windows ----------
    // ⬇️ tanksData: replace "data" with "atgReportsOnly"
    const tanksData = useMemo(() => {
        if (!atgReportsOnly || isReportsLoading) return [];
        const tanks = [];
        atgReportsOnly.forEach((report) => {
            const reportTanks = report?.state?.reported?.tanks || {};
            Object.entries(reportTanks).forEach(([tankKey, tankDetails]) => {
                const tankRecords = tankDetails?.tank_data || [];
                tankRecords.forEach((tankRecord) => {
                    tanks.push({
                        ...tankRecord,
                        tankKey,
                        reportDate: report.state.reported.system_date,
                        reportTime: report.state.reported.system_time,
                    });
                });
            });
        });
        return tanks.filter((tank) => {
            if (!tank.timestamp) return false;
            const tankDate = new Date(tank.timestamp);
            return tankDate.toISOString().split('T')[0] === selectedDateString;
        });
    }, [atgReportsOnly, selectedDate, isReportsLoading, selectedDateString]);


    const deliveriesForDay = useMemo(() => {
        const startOfDay = new Date(selectedDate).setHours(0, 0, 0, 0);
        const endOfDay = new Date(selectedDate).setHours(23, 59, 59, 999);
        return deliveries.filter((delivery) => {
            const deliveryEndTime = new Date(delivery.end_time).getTime();
            return deliveryEndTime >= startOfDay && deliveryEndTime <= endOfDay;
        });
    }, [deliveries, selectedDate]);

    // Overs/Shorts — if override enabled, compute using manual else sales/effective
    const calculateOversShorts = (tankKey) => {
        const tankRecords = tanksData.filter((tank) => tank.tankKey === tankKey);
        const sortedRecords = tankRecords.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        const startRecord = sortedRecords[0];
        const endRecord = sortedRecords[sortedRecords.length - 1];

        const startVolume = startRecord?.volume || 0;
        const endVolume = endRecord?.volume || 0;
        const volumeDiff = startVolume - endVolume;

        const deliveryForTank = deliveriesForDay.find((delivery) => delivery.tank_id === tankKey);
        const deliveryVolume = deliveryForTank?.amount || 0;

        const chosenReading = showManualOverride ? (meterReadings[tankKey] || 0) : (effectiveMeterReadings[tankKey] || 0);
        const oversShorts = volumeDiff + deliveryVolume - chosenReading;

        return { startVolume, endVolume, deliveryVolume, volumeDiff, oversShorts, meterReading: chosenReading };
    };

    // Shift window display
    // ⬇️ shift window display: replace "data" with "atgReportsOnly"
    const [filteredData, nextDayFirstRecord] = useMemo(() => {
        if (isReportsLoading || !atgReportsOnly) return [[], null];

        const shiftStartDate = new Date(selectedDate);
        shiftStartDate.setHours(shiftStartTime, 0, 0, 0);
        const shiftEndDate = new Date(shiftStartDate.getTime() + 24 * 60 * 60 * 1000);

        const extractTankRecords = (report) => {
            const tanks = report?.state?.reported?.tanks || {};
            return Object.entries(tanks).flatMap(([tankKey, tankDetails]) => {
                if (!tankDetails?.tank_data) return [];
                return tankDetails.tank_data
                    .filter((tankRecord) => tankRecord.timestamp && !isNaN(new Date(tankRecord.timestamp).getTime()))
                    .map((tankRecord) => ({
                        ...tankRecord,
                        tank_id: tankKey,
                        report_date: report.state.reported.system_date,
                        report_time: report.state.reported.system_time,
                    }));
            });
        };

        const allTankRecords = atgReportsOnly.flatMap((report) => {
            try { return extractTankRecords(report); } catch { return []; }
        });

        const dayData = allTankRecords.filter((record) => {
            const recordDate = new Date(record.timestamp);
            return recordDate >= shiftStartDate && recordDate < shiftEndDate;
        });

        dayData.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

        const nextDayData = allTankRecords.filter((record) => {
            const recordDate = new Date(record.timestamp);
            return recordDate >= shiftEndDate && recordDate < new Date(shiftEndDate.getTime() + 24 * 60 * 60 * 1000);
        });

        const nextDayFirstRecord = nextDayData.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))[0];

        return [dayData, nextDayFirstRecord];
    }, [atgReportsOnly, selectedDate, shiftStartTime, isReportsLoading]);


    // ---------- Build the exact format the PDF expects (array with metric_date + fuel_readings) ----------
    const buildMeterReadingsArrayForPDF = (sourceMap) => {
        const fuel_readings = Object.entries(sourceMap || {}).map(([tank, reading]) => ({
            fuel_type: tank,
            amount_dispensed: Number(reading || 0),
        }));
        return [{
            metric_date: selectedDate.toISOString(), // keep full ISO
            fuel_readings,
        }];
    };

    const handleInputChange = (tank, value) => {
        setMeterReadings({ ...meterReadings, [tank]: Number(value) });
    };

    const handleDownloadSIRReport = () => {
        if (!deliveries || !atgReportsOnly || atgReportsOnly.length === 0) {
            console.error('No valid ATG tank reports available to download.');
            return;
        }

        let readingsArrayForPDF = null;

        // 1) Prefer monthly POS sales if available
        if (monthlySalesReadingsArray && monthlySalesReadingsArray.length > 0) {
            const clone = JSON.parse(JSON.stringify(monthlySalesReadingsArray));
            readingsArrayForPDF = showManualOverride
                ? overrideSelectedDayInMonthlyArray(clone)
                : clone;
        } else {
            // 2) Fallback to just the selected day (what you had)
            const dayMap = showManualOverride ? meterReadings : effectiveMeterReadings;
            readingsArrayForPDF = [{
                metric_date: selectedDate.toISOString(),
                fuel_readings: Object.entries(dayMap || {}).map(([tank, reading]) => ({
                    fuel_type: tank,
                    amount_dispensed: Number(reading || 0),
                })),
            }];
        }

        generateSIRPDF(
            atgReportsOnly,     // tanks-only reports
            deliveries,
            readingsArrayForPDF,
            atgAttributes,
            selectedDate,
            logoImg,
            shiftStartTime,
            tankConfig
        );
    };

    console.log('All reports (raw): ', data?.length);
    console.log('ATG reports (tanks only): ', atgReportsOnly?.length);
    console.log('Example ATG report sample: ', atgReportsOnly?.[0]);


    const handleDownloadLeakTestReport = () => {
        const pdfInputArray = showManualOverride
            ? buildMeterReadingsArrayForPDF(meterReadings)
            : buildMeterReadingsArrayForPDF(effectiveMeterReadings);
        if (data && data.length > 0) {
            generateLeakTestPDF(data, pdfInputArray, atgAttributes, selectedDate, logoImg);
        } else {
            console.error("No valid Leak Test reports available to download.");
        }
    };

    const shiftStart = filteredData[0]?.timestamp || 'N/A';
    const shiftEnd = nextDayFirstRecord?.timestamp || 'N/A';

    const handleSave = async () => {
        // Save manual readings ONLY (sales are not persisted here)
        const formattedReadings = Object.entries(meterReadings).map(([tank, reading]) => ({
            fuel_type: tank,
            amount_dispensed: reading
        }));

        const readingsToSave = {
            organization_id,
            atg_name,
            readings: [{
                metric_date: selectedDate,
                fuel_readings: formattedReadings
            }]
        };

        try {
            await updateAtgMeterReadings(readingsToSave).unwrap();
        } catch (error) {
            console.error('Failed to save meter readings', error);
        }
    };

    // -------------------- RENDER --------------------
    let content;

    if (isReportsLoading) {
        content = (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress />
                <Typography variant="h4" sx={{ ml: 2 }}>
                    Loading Daily Summary...
                </Typography>
            </Box>
        );
    } else if (isErrorReports) {
        content = (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Typography variant="body1" color="error">
                    Error loading Daily Summary. Please try again later.
                </Typography>
            </Box>
        );
    } else if (deliveries.length === 0) {
        content = (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Typography variant="body1">No data found.</Typography>
            </Box>
        );
    } else {
        content = (
            <Paper
                elevation={3}
                sx={{
                    backgroundColor: colors.primary[700],
                    padding: '20px',
                    borderRadius: '8px',
                    height: '100%',
                    boxShadow: '0px 1px 5px rgba(0, 0, 0, 0.4)',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {/* Non-scrollable area */}
                <Box sx={{ marginBottom: '10px' }}>
                    <Typography variant="h6">{`Selected Date: ${selectedDateString}`}</Typography>
                    <Typography>
                        {`Shift Start Time: ${shiftStart || '00:00'} | Shift End Time: ${shiftEnd || '23:59'}`}
                    </Typography>

                    {/* status + override toggle */}
                    {isFetchingPosSales ? (
                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                            Loading POS sales…
                        </Typography>
                    ) : salesDerivedReadings && !showManualOverride ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            <Typography variant="body2" sx={{ color: colors.greenAccent[300] }}>
                                Using POS sales for SIR (midgrade split 50/50).
                            </Typography>
                            <Button
                                size="small"
                                variant="text"
                                onClick={() => setShowManualOverride(true)}
                                sx={{ px: 1, minWidth: 'auto', color: colors.redAccent[300] }}
                            >
                                Override (manual)
                            </Button>
                        </Box>
                    ) : salesDerivedReadings && showManualOverride ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            <Typography variant="body2" sx={{ color: colors.yellowAccent?.[300] || '#ffde7a' }}>
                                Manual override enabled — your inputs will be used for SIR and can be saved.
                            </Typography>
                            <Button
                                size="small"
                                variant="text"
                                onClick={() => setShowManualOverride(false)}
                                sx={{ px: 1, minWidth: 'auto' }}
                            >
                                Cancel override
                            </Button>
                        </Box>
                    ) : (
                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                            POS sales not available — using manual meter readings.
                        </Typography>
                    )}
                </Box>

                {/* Scrollable area including optional Save button */}
                <Box sx={{ flex: 1, overflowY: 'auto' }}>
                    <Grid container spacing={1}>
                        {Object.entries(
                            tanksData.reduce((acc, tank) => {
                                acc[tank.tankKey] = acc[tank.tankKey] || [];
                                acc[tank.tankKey].push(tank);
                                return acc;
                            }, {})
                        ).map(([tankKey]) => {
                            const { startVolume, endVolume, deliveryVolume, volumeDiff, oversShorts, meterReading } =
                                calculateOversShorts(tankKey);

                            const label = productLabels[tankKey] || tankKey.toUpperCase();
                            const usingSales = !!(salesDerivedReadings && !showManualOverride && salesDerivedReadings[tankKey] != null);

                            return (
                                <Grid item xs={12} sm={12} md={12} lg={6} xl={4} key={tankKey}>
                                    <Paper
                                        elevation={2}
                                        sx={{
                                            backgroundColor: colors.primary[600],
                                            padding: '15px',
                                            borderRadius: '8px',
                                            boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.3)',
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'space-between',
                                        }}
                                    >
                                        {/* Tank Data */}
                                        <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {label}
                                        </Typography>

                                        <Typography sx={{ wordWrap: 'break-word' }}>{`Start Volume: ${startVolume}`}</Typography>
                                        <Typography sx={{ wordWrap: 'break-word' }}>{`End Volume: ${endVolume}`}</Typography>

                                        {deliveryVolume > 0 && (
                                            <Typography sx={{ color: 'green', wordWrap: 'break-word' }}>
                                                {`Delivery: ${deliveryVolume} gallons`}
                                            </Typography>
                                        )}

                                        <Typography sx={{ wordWrap: 'break-word' }}>{`Volume Difference: ${volumeDiff}`}</Typography>

                                        <Typography sx={{ overflowWrap: 'break-word', wordWrap: 'break-word', whiteSpace: 'normal' }}>
                                            {`Overs/Shorts: ${startVolume} - ${endVolume} + ${deliveryVolume} = ${volumeDiff + deliveryVolume} (Book) - ${meterReading || 0} (Sales${showManualOverride ? '/Manual' : ''}) = ${oversShorts} gallons`}
                                        </Typography>

                                        {/* Manual inputs:
                        - hidden if sales exist and override is OFF
                        - shown if override is ON
                        - always shown when no POS sales available
                    */}
                                        {(!salesDerivedReadings || showManualOverride) && (
                                            <TextField
                                                fullWidth
                                                type="number"
                                                label={salesDerivedReadings ? 'Meter Reading (Manual Override)' : 'Meter Reading'}
                                                value={meterReadings[tankKey] ?? ''}
                                                onChange={(e) => handleInputChange(tankKey, e.target.value)}
                                                margin="dense"
                                                helperText={
                                                    salesDerivedReadings
                                                        ? 'Override is ON: manual value will be used for SIR.'
                                                        : 'No POS sales — manual input will be used for SIR.'
                                                }
                                                sx={{
                                                    mt: 1,
                                                    backgroundColor: colors.primary[500],
                                                    '& .MuiOutlinedInput-root': { borderRadius: '8px' },
                                                }}
                                            />
                                        )}
                                    </Paper>
                                </Grid>
                            );
                        })}
                    </Grid>

                    {/* Save only appears when manual override visible or POS absent */}
                    {(!salesDerivedReadings || showManualOverride) && (
                        <Box sx={{ marginTop: '20px' }}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleSave}
                                sx={{
                                    width: '100%',
                                    backgroundColor: colors.primary[500],
                                    '&:hover': { backgroundColor: colors.primary[700] },
                                }}
                            >
                                Save Manual Readings
                            </Button>
                        </Box>
                    )}
                </Box>
            </Paper>
        );
    }

    return (
        <Paper
            style={{
                marginTop: '0px',
                padding: '10px',
                height: '500px',
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            }}
        >
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} sx={{ borderBottom: '1px solid #ddd', pb: 2 }}>
                <Typography variant="h4" gutterBottom>
                    Daily Summary
                </Typography>
                <Box>
                    <Tooltip
                        title={isFetchingPosSalesMonth ? 'Building month sales…' : 'Download SIR Report'}
                    >
                        <span>
                            <Button
                                variant="outlined"
                                color="secondary"
                                onClick={handleDownloadSIRReport}
                                disabled={sirDisabled}
                                sx={{ mr: 1 }}
                                startIcon={isFetchingPosSalesMonth ? <CircularProgress size={16} /> : null}
                            >
                                {isFetchingPosSalesMonth ? 'Preparing…' : 'Download SIR Report'}
                            </Button>
                        </span>
                    </Tooltip>
                    <Tooltip title="Leak Test Report is not available">
                        <Button
                            variant="outlined"
                            sx={{
                                color: colors.redAccent[200],
                                borderColor: colors.redAccent[200],
                                '&:hover': { backgroundColor: colors.redAccent[100] },
                            }}
                        >
                            Download Leak Test Report
                        </Button>
                    </Tooltip>
                </Box>
            </Box>
            <Box sx={{ flex: 1, overflowY: 'auto' }}>
                {isReportsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        <CircularProgress />
                        <Typography variant="h4" sx={{ ml: 2 }}>
                            Loading Daily Summary...
                        </Typography>
                    </Box>
                ) : (
                    content
                )}
            </Box>
        </Paper>
    );
};

export default DailySummary;
